import { inject, Injectable } from '@angular/core';
import { isNil } from 'lodash';
import Computer, { AgentType, AgentTypeAny, GetComputersParams, StorageConnection } from 'mbs-ui-shared@models/Computer';
import { AgentCommand } from 'mbs-ui-shared@models/ComputersModals';
import { InstallationAgents } from 'mbs-ui-shared@models/InstallAgent';
import { PlanFormatTypes, PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { AvailablePlanStartModes } from 'mbs-ui-shared@models/backup/available-plan-start-modes';
import { AvailablePlanTypes, KindPlanEnum } from 'mbs-ui-shared@models/backup/available-plan-types';
import {
  CreateRecoveryDiskParams,
  CreateRecoveryDiskResult,
  CreateRecoveryDiskStatus
} from 'mbs-ui-shared@models/backup/create-recovery-disk';
import { DiagnosticLog } from 'mbs-ui-shared@models/backup/diagnostic-log-model';
import {
  KvmMachineAction,
  KvmMachineDeleteParams,
  KvmMachineRdpConsoleOpenParams,
  KvmMachineRdpConsoleOpenResult,
  KvmMachineRdpSessionsData,
  KvmMachineScreenshot,
  KvmMachineScreenshotParams,
  KvmMachinesData,
  KvmMachineStartParams,
  KvmMachineStopParams
} from 'mbs-ui-shared@models/backup/kvm-machine';
import { NetworkCredentials } from 'mbs-ui-shared@models/backup/network-credential';
import { PlanHistoryData, PlanHistoryDetails } from 'mbs-ui-shared@models/backup/plan-history-model';
import { PlanInfo, PlanInfoData } from 'mbs-ui-shared@models/backup/plan-info-model';
import { RecoveryDiskCapabilities } from 'mbs-ui-shared@models/backup/recovery-disk-capabilities';
import { RecoveryDiskTarget } from 'mbs-ui-shared@models/backup/recovery-disk-target';
import {
  AgentRemoteCommandType,
  InstallBuildsType,
  ParamsForceUpdate,
  RemoteCommandResponse,
  RemoteCommandType,
  RemovePlansWithDataReturnType,
  RepositorySyncState,
  UpdateBuild
} from 'mbs-ui-shared@models/rm/remote-command-model';
import { map, Observable } from 'rxjs';
import { RmCommandsService } from './rm-commands.service';

@Injectable()
export class RmCommandsWrapper {
  private rmCommands = inject<RmCommandsService>(RmCommandsService);

  installPublicAgentByComputer(
    payload: { computer: Computer; appId: AgentType; installedAgent: AgentType },
    async = false
  ): Observable<any> {
    return this.installAgent(
      {
        computerHid: payload.computer.hid,
        installedAgent: payload.installedAgent,
        builds: [{ appId: payload.appId, versionType: InstallBuildsType.Public }]
      },
      async
    );
  }

  installPublicAgentByComputers(payload: { computers: Computer[]; appId: AgentType }, async = false): Observable<any> {
    return this.groupAgentInstall(
      {
        computers: payload.computers.map((computer) => ({
          hid: computer.hid,
          installedAgent: computer.apps.find((app) => InstallationAgents.includes(app.applicationId)).applicationId
        })),
        builds: [{ appId: payload.appId, versionType: InstallBuildsType.Public }]
      },
      async
    );
  }

  installAgent(
    payload: {
      computerHid: string;
      installedAgent: AgentType;
      builds: {
        appId: AgentType;
        versionType: InstallBuildsType;
      }[];
    },
    async = false
  ): Observable<any> {
    if (async) {
      return this.groupAgentInstall(
        {
          computers: [
            {
              hid: payload.computerHid,
              installedAgent: payload.installedAgent
            }
          ],
          builds: payload.builds
        },
        async
      );
    }

    return this.rmCommands.remoteCommandByHid(payload.computerHid, {
      commandType: RemoteCommandType.InstallApp,
      agentType: payload.installedAgent,
      params: {
        installBuilds: payload.builds
      }
    });
  }

  groupAgentInstall(
    payload: {
      computers: { hid: string; installedAgent: AgentType }[];
      builds: {
        appId: AgentType;
        versionType: InstallBuildsType;
      }[];
    },
    async = false
  ): Observable<any> {
    return this.rmCommands.groupRemoteCommand(
      {
        commandType: RemoteCommandType.InstallApp,
        agentCommands: payload.computers.map((computer) => {
          return {
            computerHid: computer.hid,
            agentType: computer.installedAgent,
            params: {
              installBuilds: payload.builds
            }
          };
        })
      },
      async
    );
  }

  bulkInstallPublicAgent(filter: GetComputersParams, appId: AgentType, async = false): Observable<any> {
    return this.bulkInstallAgent(filter, appId, InstallBuildsType.Public, async);
  }

  bulkInstallAgent(filter: GetComputersParams, appId: AgentType, versionType: InstallBuildsType, async = false): Observable<any> {
    return this.rmCommands.bulkRemoteCommand(
      {
        commandType: RemoteCommandType.InstallApp,
        filter,
        appIds: [AgentTypeAny],
        params: {
          installBuilds: [{ appId, versionType }]
        }
      },
      async
    );
  }

  updateAgent(payload: { computerHid: string; agentType: AgentType; versionType: InstallBuildsType }): Observable<any> {
    return this.rmCommands.groupRemoteCommand({
      commandType: RemoteCommandType.ForceUpdate,
      agentCommands: [
        {
          computerHid: payload.computerHid,
          agentType: payload.agentType,
          params: {
            updateBuilds: [
              {
                appId: payload.agentType,
                versionType: payload.versionType
              }
            ]
          }
        }
      ]
    });
  }

  uninstallAgentsByComputer(payload: { computer: Computer; appIds: string[] | AgentType[] }): Observable<any> {
    return this.uninstallAgents({ computerHid: payload.computer.hid, appIds: payload.appIds });
  }

  uninstallAgentByComputer(payload: { computer: Computer; appId: string | AgentType }): Observable<any> {
    return this.uninstallAgents({ computerHid: payload.computer.hid, appIds: [payload.appId] });
  }

  uninstallAgent(payload: { computerHid: string; appId: string | AgentType }): Observable<any> {
    return this.uninstallAgents({ computerHid: payload.computerHid, appIds: [payload.appId] });
  }

  uninstallAgents(payload: { computerHid: string; appIds: string[] | AgentType[] }): Observable<any> {
    return this.rmCommands.uninstallAgents({ hid: payload.computerHid, appIds: this.prepareAppIds(payload.appIds) });
  }

  regenerateClientHID(payload: { computerHid: string; appId: AgentType }): Observable<unknown> {
    return this.rmCommands.remoteCommandByHid(payload.computerHid, {
      agentType: payload.appId,
      commandType: RemoteCommandType.RegenerateClientHID
    });
  }

  getComputerPlans(hid: string): Observable<PlanInfoData> {
    return this.rmCommands
      .remoteCommandByHid<PlanInfoData>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetPlanInfoList,
        params: {} as any
      })
      .pipe(map((p) => p?.data || { planInfos: [], timeZoneOffset: 0 }));
  }

  getPlanInfo(hid: string, planId: string): Observable<PlanInfo> {
    return this.rmCommands
      .remoteCommandByHid<{ planInfo: PlanInfo; timeZoneOffset: number }>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetPlanInfo,
        params: { planId }
      })
      .pipe(map((response) => response?.data?.planInfo ?? null));
  }

  // No empty-list fallback like getComputerPlans has: hostAnswered = false with no machines is an outage, not "no machines"
  getComputerVirtualMachines(hid: string): Observable<KvmMachinesData> {
    return this.rmCommands
      .remoteCommandByHid<KvmMachinesData>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetKvmMachines,
        params: {}
      })
      .pipe(map((response) => response?.data));
  }

  startKvmMachine(hid: string, params: KvmMachineStartParams): Observable<KvmMachineAction> {
    return this.kvmMachineAction(hid, RemoteCommandType.StartKvmMachine, params);
  }

  stopKvmMachine(hid: string, params: KvmMachineStopParams): Observable<KvmMachineAction> {
    return this.kvmMachineAction(hid, RemoteCommandType.StopKvmMachine, params);
  }

  deleteKvmMachine(hid: string, params: KvmMachineDeleteParams): Observable<KvmMachineAction> {
    return this.kvmMachineAction(hid, RemoteCommandType.DeleteKvmMachine, params);
  }

  getRdpMachineConsoles(hid: string): Observable<KvmMachineRdpSessionsData> {
    return this.rmCommands
      .remoteCommandByHid<KvmMachineRdpSessionsData>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetRDPMachineConsoles,
        params: {}
      })
      .pipe(map((response) => response?.data));
  }

  openRdpMachineConsole(hid: string, params: KvmMachineRdpConsoleOpenParams): Observable<KvmMachineRdpConsoleOpenResult> {
    return this.rmCommands
      .remoteCommandByHid<KvmMachineRdpConsoleOpenResult>(
        hid,
        { agentType: AgentType.Backup, commandType: RemoteCommandType.OpenRDPMachineConsole, params },
        true
      )
      .pipe(map((response) => response?.data));
  }

  closeRdpMachineConsole(hid: string, sessionId: string): Observable<boolean> {
    return this.rmCommands
      .remoteCommandByHid(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.CloseRDPMachineConsole,
        params: { sessionId }
      })
      .pipe(map(Boolean));
  }

  getKvmMachineScreenshot(hid: string, params: KvmMachineScreenshotParams): Observable<KvmMachineScreenshot> {
    return this.rmCommands
      .remoteCommandByHid<KvmMachineScreenshot>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetKvmMachineScreenshot,
        params
      })
      .pipe(map((response) => response?.data));
  }

  getRecoveryDiskCapabilities(computer: Computer): Observable<RecoveryDiskCapabilities> {
    return this.rmCommands
      .remoteCommand<RecoveryDiskCapabilities>(computer, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetRecoveryDiskCapabilities,
        params: {}
      })
      .pipe(map((response) => response?.data));
  }

  getRecoveryDiskTargets(computer: Computer): Observable<RecoveryDiskTarget[]> {
    return this.rmCommands
      .remoteCommand<RecoveryDiskTarget[]>(computer, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetRecoveryDiskTargets,
        params: {}
      })
      .pipe(map((response) => response?.data ?? []));
  }

  createRecoveryDisk(computer: Computer, params: CreateRecoveryDiskParams): Observable<CreateRecoveryDiskResult> {
    return this.rmCommands
      .remoteCommand<CreateRecoveryDiskResult>(computer, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.CreateRecoveryDisk,
        params
      })
      .pipe(map((response) => response?.data));
  }

  getCreateRecoveryDiskStatus(computer: Computer, operationId: string): Observable<CreateRecoveryDiskStatus> {
    return this.rmCommands
      .remoteCommand<CreateRecoveryDiskStatus>(computer, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetCreateRecoveryDiskStatus,
        params: { OperationId: operationId }
      })
      .pipe(map((response) => response?.data));
  }

  getServiceAccountName(computer: Computer, agentType: AgentType = AgentType.Backup): Observable<string> {
    return this.rmCommands
      .remoteCommand<string>(computer, {
        agentType: agentType,
        commandType: RemoteCommandType.GetAccForBackupOnlineService
      })
      .pipe(map((r) => r.data));
  }

  changeServiceAccountName(
    computer: Computer,
    account: { accountName: string; accountPassword?: string },
    agentType: AgentType = AgentType.Backup
  ): Observable<boolean> {
    return this.rmCommands
      .remoteCommand<string>(computer, {
        agentType: agentType,
        commandType: RemoteCommandType.ChangeAccForBackupOnlineService,
        params: account
      })
      .pipe(map(Boolean));
  }

  startPlan(computer: Computer, id: string, mode: AvailablePlanStartModes = AvailablePlanStartModes.Regular): Observable<boolean> {
    return this.rmCommands
      .remoteCommand<string>(computer, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.StartPlan,
        params: { PlanId: id, Mode: mode }
      })
      .pipe(map(Boolean));
  }

  stopPlan(computer: Computer, id: string): Observable<boolean> {
    return this.rmCommands
      .remoteCommand<string>(computer, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.StopPlan,
        params: { PlanId: id, Force: false }
      })
      .pipe(map(Boolean));
  }

  removePlan(computer: Computer, id: string): Observable<boolean> {
    return this.rmCommands
      .remoteCommand<string>(computer, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.RemovePlan,
        params: { PlanId: id }
      })
      .pipe(map(Boolean));
  }

  removePlansWithData(computer: Computer, ids: string[]): Observable<RemoteCommandResponse<RemovePlansWithDataReturnType>> {
    return this.rmCommands.remoteCommand<RemovePlansWithDataReturnType>(computer, {
      agentType: AgentType.Backup,
      commandType: RemoteCommandType.DeleteBackupAndPlan,
      params: { PlanIdsToDelete: ids }
    });
  }

  clonePlan(computer: Computer, id: string, name: string): Observable<boolean> {
    return this.rmCommands
      .remoteCommand<string>(computer, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.ClonePlan,
        params: { PlanId: id, NewName: name }
      })
      .pipe(map(Boolean));
  }

  private kvmMachineAction(hid: string, commandType: RemoteCommandType, params: AgentRemoteCommandType): Observable<KvmMachineAction> {
    return this.rmCommands
      .remoteCommandByHid<{ action: KvmMachineAction }>(hid, { agentType: AgentType.Backup, commandType, params }, true)
      .pipe(map((response) => response?.data?.action ?? null));
  }

  private prepareAppIds(agents: string[] | AgentType[]): string {
    return (agents as string[]).reduce((acc, item) => {
      return acc ? `appId=${item}` + `&${acc}` : `appId=${item}`;
    }, '');
  }

  getStorageConnections(profile: string, hid: string): Observable<RemoteCommandResponse<Array<StorageConnection>>> {
    return this.rmCommands.remoteCommandByHid<Array<StorageConnection>>(hid, {
      agentType: AgentType.Backup,
      commandType: RemoteCommandType.GetStorageConnections,
      params: { profile }
    });
  }

  saveStorageConnections(
    data: {
      storageId: string;
      passShare: string;
      loginShare: null;
      displayName: string;
      path: string;
      prefix: string;
    },
    hid: string
  ): Observable<RemoteCommandResponse<string>> {
    return this.rmCommands.remoteCommandByHid<string>(hid, {
      agentType: AgentType.Backup,
      commandType: RemoteCommandType.SaveFileSystemSettings,
      params: { ...data }
    });
  }

  deleteStorageConnection(profile: string, hid: string, storageId: string): Observable<RemoteCommandResponse<boolean>> {
    return this.rmCommands.remoteCommandByHid<boolean>(hid, {
      agentType: AgentType.Backup,
      commandType: RemoteCommandType.DeleteFileSystemStorage,
      params: { storageId: storageId, profile: profile }
    });
  }

  getNetworkCredentialsList(hid: string): Observable<NetworkCredentials[]> {
    return this.rmCommands
      .remoteCommandByHid<NetworkCredentials[]>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetNetworkCredentialsList,
        params: {} as any
      })
      .pipe(map((p) => (p && p.data) || []));
  }
  addNetworkCredentials(
    hid: string,
    params: { path: string; login: string; password: string; check: boolean }
  ): Observable<NetworkCredentials> {
    return this.rmCommands
      .remoteCommandByHid<NetworkCredentials>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.AddNetworkCredentials,
        params
      })
      .pipe(map((p) => p?.data));
  }
  deleteNetworkCredentials(hid: string, path: string): Observable<boolean> {
    return this.rmCommands
      .remoteCommandByHid<void>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.DeleteNetworkCredentials,
        params: {
          path
        } as any
      })
      .pipe(map(Boolean));
  }
  checkNetworkCredentials(hid: string, params: { path: string; login: string; password: string }): Observable<NetworkCredentials> {
    return this.rmCommands
      .remoteCommandByHid<NetworkCredentials>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.CheckNetworkCredentials,
        params
      })
      .pipe(map((p) => p?.data));
  }

  getAvailablePlanTypes(hid: string): Observable<AvailablePlanTypes> {
    return this.rmCommands
      .remoteCommandByHid<{ availablePlanTypes: AvailablePlanTypes }>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetAvailablePlanTypes,
        params: {}
      })
      .pipe(
        map((p) => {
          if (p?.data?.availablePlanTypes?.length) {
            if (!isNil(p?.data?.availablePlanTypes[0].type)) {
              return p?.data?.availablePlanTypes.map((el) => {
                el.planType = el.type;
                return el;
              });
            }
            return p?.data?.availablePlanTypes.map((el) => ({
              kind: KindPlanEnum[el.kind],
              planType: PlanTypes[el.planType],
              format: PlanFormatTypes[el.format] || 0
            }));
          }
          return [];
        })
      );
  }

  groupForceUpdate(agentCommands: AgentCommand<ParamsForceUpdate>[]): Observable<any> {
    return this.rmCommands.groupRemoteCommand(
      {
        commandType: RemoteCommandType.ForceUpdate,
        agentCommands
      },
      true
    );
  }

  bulkForceUpdate(filter: GetComputersParams, updateBuilds: UpdateBuild[], appIds: AgentType[]): Observable<any> {
    return this.rmCommands.bulkRemoteCommand(
      {
        commandType: RemoteCommandType.ForceUpdate,
        appIds,
        filter,
        params: { updateBuilds }
      },
      true
    );
  }

  switchWebSettings(hid: string, state: boolean): Observable<{ ok: boolean }> {
    return this.rmCommands.remoteCommandByHid(hid, {
      agentType: AgentType.Backup,
      commandType: RemoteCommandType.ConfigureService,
      params: { EnableWebSettings: state }
    });
  }

  updateConnectConfig(filter: GetComputersParams, appId: AgentType): Observable<any> {
    return this.rmCommands.bulkRemoteCommand({
      commandType: RemoteCommandType.UpdateConfig,
      filter,
      appIds: [appId]
    });
  }

  getSyncStatus(hid: string): Observable<{ state: RepositorySyncState }> {
    return this.rmCommands.getSyncStatus(hid);
  }

  groupDeleteComputers(computerHids: string[]): Observable<any> {
    return this.rmCommands.groupRemoteCommand(
      {
        commandType: RemoteCommandType.DeleteComputer,
        agentCommands: computerHids.map((computerHid) => {
          return {
            computerHid,
            agentType: AgentType.Backup // does not matter which agent type to use for delete computer command
          };
        })
      },
      false
    );
  }

  bulkDeleteComputers(filter: GetComputersParams): Observable<any> {
    return this.rmCommands.bulkRemoteCommand(
      {
        commandType: RemoteCommandType.DeleteComputer,
        filter,
        appIds: [AgentTypeAny] // does not matter which agent type to use for delete computer command
      },
      false
    );
  }

  getPlansHistory(
    hid: string,
    params: { PlanId?: string; Limit?: number; Offset?: number; FromUtc?: string; ToUtc?: string; IsAsync?: boolean }
  ): Observable<{ items: Array<PlanHistoryData>; timeZoneOffset: number; totalCount: number }> {
    return this.rmCommands
      .remoteCommandByHid<{ items: Array<PlanHistoryData>; timeZoneOffset: number; totalCount: number }>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetPlansHistory,
        params
      })
      .pipe(map((p) => p?.data));
  }

  getPlanHistoryDetails(
    hid: string,
    params: { SessionId: string; Year: number; Month: number; IsAsync?: boolean }
  ): Observable<PlanHistoryDetails> {
    return this.rmCommands
      .remoteCommandByHid<PlanHistoryDetails>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetPlanHistoryDetails,
        params
      })
      .pipe(map((p) => p?.data));
  }

  getBackupAgentLog(hid: string, params: Record<string, string | number | boolean>): Observable<DiagnosticLog> {
    return this.rmCommands
      .remoteCommandByHid<DiagnosticLog>(hid, {
        agentType: AgentType.Backup,
        commandType: RemoteCommandType.GetLog,
        params
      })
      .pipe(map((p) => p?.data));
  }
}
